import { useState } from 'react'
import { colors } from '../../../../colors'
import { MAX_IMAGE_ATTACHMENTS } from '../../../../constants'
import photo from '../../../../images/photo.png'
import { BargainUserInfo } from '../../../../types'
import CharacterCounter from '../../../ui/CharacterCounter'

interface CommentFieldProps {
  value: string
  onChange: (value: string) => void
  images: File[]
  imagePreviews: string[]
  loading: boolean
  onImageChange: (files: FileList) => void
  onRemoveImage: (index: number) => void
  bargain_user_info: BargainUserInfo
  labelWidth?: number
}

const MAX_LENGTH = 200

const styles: Record<string, React.CSSProperties> = ({
  formGroup: {
    display: 'flex',
    alignItems: 'flex-start',
    marginBottom: '16px',
  },
  label: {
    display: 'block',
    fontWeight: 'bold',
    fontSize: '16px',
    marginBottom: '4px',
    pointerEvents: 'none',
    flexShrink: 0,
    marginRight: '8px',
  },
  required: {
    color: '#e74c3c',
    marginLeft: '4px',
  },
  contentArea: {
    flex: 1,
  },
  textareaContainer: {
    position: 'relative',
    border: '1px solid #ddd',
    borderRadius: '4px',
  },
  textarea: {
    width: '100%',
    padding: '10px',
    fontSize: '16px',
    border: 'none',
    height: '150px',
    resize: 'vertical',
    outline: 'none',
  },
  bottomBar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '8px 10px',
  },
  imageUploadWrapper: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginTop: '8px',
  },
  imageButtonWrapper: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    width: '30%',
    aspectRatio: '1 / 1',
    backgroundColor: '#f8f8f8',
    border: '1px solid #e3e3e3',
    borderRadius: '4px',
    cursor: 'pointer',
    position: 'relative',
    transition: 'border-color 0.2s',
    boxSizing: 'border-box',
    outline: 'none',
    padding: 0,
  },
  imageButtonIcon: {
    width: '24px',
  },
  imageButtonCount: {
    color: colors['keijiban-coral'],
    fontSize: '12px',
    fontWeight: 500,
    lineHeight: '1',
    textAlign: 'center',
    marginTop: '4px',
    letterSpacing: '2px',
  },
  hiddenInput: {
    display: 'none',
  },
  imagePreviews: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
    marginTop: '8px',
  },
  imagePreviewContainer: {
    position: 'relative',
    width: '30%',
    aspectRatio: '1 / 1',
    borderRadius: '4px',
    overflow: 'hidden',
  },
  imagePreview: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  removeButton: {
    position: 'absolute',
    top: '4px',
    right: '4px',
    width: '20px',
    height: '20px',
    borderRadius: '50%',
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    color: 'white',
    border: 'none',
    cursor: 'pointer',
    fontSize: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  loadingIndicator: {
    fontSize: '12px',
    color: colors['text-secondary'],
  },
  requiredNotice: {
    color: '#e74c3c',
    fontSize: '14px',
    fontWeight: 400,
    marginBottom: '8px',
    marginLeft: 0,
  },
  // 全画面プレビュー用スタイル
  fullscreenOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    cursor: 'pointer',
  },
  fullscreenImage: {
    maxWidth: '100vw',
    maxHeight: '80vh',
    objectFit: 'contain',
  },
  previewContainer: {
    position: 'relative',
  },
  closeButton: {
    position: 'fixed',
    top: '20px',
    right: '20px',
    fontSize: '40px',
    color: 'white',
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
    width: '50px',
    height: '50px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  clickableImagePreview: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    cursor: 'pointer',
  },
})

const CommentField: React.FC<CommentFieldProps> = ({
  value,
  onChange,
  images,
  imagePreviews,
  loading,
  onImageChange,
  onRemoveImage,
}) => {
  const [selectedImageIndex, setSelectedImageIndex] = useState<number | null>(null)

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (files) {
      onImageChange(files)
    }
  }

  const handleImageClick = (index: number) => {
    setSelectedImageIndex(index)
  }

  const handleClosePreview = () => {
    setSelectedImageIndex(null)
  }

  return (
    <div style={styles.formGroup}>
      {/* コンテンツエリア */}
      <div style={styles.contentArea}>
        {/* 必須注意文言 */}
        <label style={styles.label}>コメント・画像
          <span style={styles.requiredNotice}> *どちらか必須</span>
        </label>
        

        {/* コメント入力エリア */}
        <div style={styles.textareaContainer}>
          <textarea
            value={value}
            onChange={(e) => {
              const newValue = e.target.value
              if (newValue.length <= MAX_LENGTH) {
                onChange(newValue)
              }
            }}
            placeholder="家族のお気に入り！毎週買ってます！"
            style={styles.textarea}
            maxLength={MAX_LENGTH}
            required
          />

          <div style={styles.bottomBar}>
            <CharacterCounter currentLength={value.length} maxLength={MAX_LENGTH} />
          </div>
        </div>

        {/* 画像アップロード */}
        <div style={styles.imageUploadWrapper}>
          {/* 画像プレビュー */}
          {imagePreviews.map((preview, index) => (
            <div key={index} style={styles.imagePreviewContainer}>
              <img 
                src={preview} 
                alt="プレビュー" 
                style={styles.clickableImagePreview}
                onClick={() => handleImageClick(index)}
              />
              <button
                type="button"
                onClick={() => { onRemoveImage(index) }}
                style={styles.removeButton}
              >
                ×
              </button>
            </div>
          ))}

          {images.length < MAX_IMAGE_ATTACHMENTS && (
            <label
              htmlFor="image-upload-input"
              style={styles.imageButtonWrapper}
              tabIndex={0}
            >
              <img src={photo} alt="画像" style={styles.imageButtonIcon} />
              <div style={styles.imageButtonCount}>{MAX_IMAGE_ATTACHMENTS}枚</div>
              <input
                id="image-upload-input"
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                style={styles.hiddenInput}
                multiple={false}
              />
            </label>
          )}
          {loading && <span style={styles.loadingIndicator}>処理中...</span>}
        </div>

      </div>

      {/* 全画面プレビューモーダル */}
      {selectedImageIndex !== null && (
        <div style={styles.fullscreenOverlay} onClick={handleClosePreview}>
          <div style={styles.previewContainer}>
            <img
              src={imagePreviews[selectedImageIndex]}
              alt="全画面プレビュー"
              style={styles.fullscreenImage}
            />
            <button
              type="button"
              style={styles.closeButton}
              onClick={handleClosePreview}
              aria-label="プレビューを閉じる"
            >
              ×
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export default CommentField
